import * as Cesium from 'cesium';
import { APP_CONFIG } from '../config/appConfig';
import { DATA_SOURCES } from '../config/dataSources';

export interface OrthoLoadResult {
  layer: Cesium.ImageryLayer;
  isFallback: boolean;
  source: string;
  setVisible: (visible: boolean) => void;
}

function addImagery(
  viewer: Cesium.Viewer,
  url: string,
  credit: string,
): Cesium.ImageryLayer {
  const provider = new Cesium.OpenStreetMapImageryProvider({
    url,
    credit,
  });
  return addEnhancedProvider(viewer, provider);
}

function addTemplateImagery(
  viewer: Cesium.Viewer,
  url: string,
  credit: string,
): Cesium.ImageryLayer {
  const provider = new Cesium.UrlTemplateImageryProvider({
    url,
    credit,
    rectangle: Cesium.Rectangle.fromDegrees(
      APP_CONFIG.orthoCoverage.west,
      APP_CONFIG.orthoCoverage.south,
      APP_CONFIG.orthoCoverage.east,
      APP_CONFIG.orthoCoverage.north,
    ),
    minimumLevel: APP_CONFIG.orthoCoverage.minimumLevel,
    maximumLevel: APP_CONFIG.orthoCoverage.maximumLevel,
  });
  return addEnhancedProvider(viewer, provider);
}

function addEnhancedProvider(
  viewer: Cesium.Viewer,
  provider: Cesium.ImageryProvider,
): Cesium.ImageryLayer {
  // 斜め視点の補間品質を上げつつ、航空写真の色は過度に加工しない。
  const layer = new Cesium.ImageryLayer(provider, {
    maximumAnisotropy: 16,
    brightness: 1.03,
    contrast: 1.04,
    saturation: 1.03,
    gamma: 1.0,
  });
  viewer.imageryLayers.add(layer);
  return layer;
}

export async function loadOrthophoto(
  viewer: Cesium.Viewer,
  onError: (error: unknown, replacement: string) => void,
): Promise<OrthoLoadResult> {
  let orthoLayer: Cesium.ImageryLayer | undefined;
  let fallbackStage = 0;

  const switchImagery = (
    error: unknown,
    replacement: string,
    url: string,
    credit: string,
    template: boolean,
  ): void => {
    if (fallbackStage >= (replacement === 'GSI航空写真' ? 1 : 2)) return;
    fallbackStage += 1;
    onError(error, replacement);
    if (orthoLayer) {
      viewer.imageryLayers.remove(orthoLayer, true);
    }
    orthoLayer = template
      ? addTemplateImagery(viewer, url, credit)
      : addImagery(viewer, url, credit);
    if (replacement === 'GSI航空写真') {
      const provider = orthoLayer.imageryProvider as Cesium.UrlTemplateImageryProvider;
      provider.errorEvent.addEventListener((providerError) => {
        switchImagery(
          providerError,
          'OpenStreetMapへ切替',
          DATA_SOURCES.fallbackImagery,
          '© OpenStreetMap contributors',
          false,
        );
      });
    }
    viewer.scene.requestRender();
  };

  try {
    const provider = new Cesium.UrlTemplateImageryProvider({
      url: DATA_SOURCES.ortho,
      credit: 'PLATEAU-Ortho / 国土交通省',
      rectangle: Cesium.Rectangle.fromDegrees(
        APP_CONFIG.orthoCoverage.west,
        APP_CONFIG.orthoCoverage.south,
        APP_CONFIG.orthoCoverage.east,
        APP_CONFIG.orthoCoverage.north,
      ),
      minimumLevel: APP_CONFIG.orthoCoverage.minimumLevel,
      maximumLevel: APP_CONFIG.orthoCoverage.maximumLevel,
    });
    provider.errorEvent.addEventListener((error) =>
      switchImagery(
        error,
        'GSI航空写真',
        DATA_SOURCES.gsiAerial,
        '国土地理院 航空写真',
        true,
      ),
    );
    orthoLayer = addEnhancedProvider(viewer, provider);
    viewer.scene.requestRender();
    return {
      layer: orthoLayer,
      isFallback: false,
      source: 'PLATEAU-Ortho',
      setVisible: (visible) => {
        if (orthoLayer) orthoLayer.show = visible;
        viewer.scene.requestRender();
      },
    };
  } catch (error) {
    switchImagery(
      error,
      'GSI航空写真',
      DATA_SOURCES.gsiAerial,
      '国土地理院 航空写真',
      true,
    );
    const fallbackLayer = orthoLayer ?? addTemplateImagery(
      viewer,
      DATA_SOURCES.gsiAerial,
      '国土地理院 航空写真',
    );
    return {
      layer: fallbackLayer,
      isFallback: true,
      source: 'GSI航空写真',
      setVisible: (visible) => {
        fallbackLayer.show = visible;
        viewer.scene.requestRender();
      },
    };
  }
}
